import { useEffect, useMemo, useState } from 'react';
import {
  useApi,
  identityApiRef,
  useRouteRef,
} from '@backstage/frontend-plugin-api';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  Header,
  Container,
  Table,
  useTable,
  CellText,
  Cell,
  Flex,
  Button,
  ButtonLink,
  Box,
  type ColumnConfig,
  type SortDescriptor,
} from '@backstage/ui';
import { RiAddLine, RiCircleFill, RiDownloadLine } from '@remixicon/react';
import type {
  TrackDefinition,
  TrackImportResult,
} from '@internal/backstage-plugin-pulse-common';
import {
  useCreateTrack,
  usePulseTracks,
  useUrlMultiSelect,
  useUrlValue,
  useResetUrlParams,
  useMyTeams,
} from '../../hooks';
import { DefinitionListFilterBar } from '../definition-list-filter-bar';
import {
  getOwnerLabel,
  getOwnerOptions,
  getAffectedKindOptions,
  matchesAffectedKinds,
} from '../definition-facets';
import { tracksDetailsSubRouteRef, tracksNewSubRouteRef } from '../../routes';
import { paginationOptions, sortCollator } from '../config';
import { PulseListSkeleton } from '../list-skeleton';
import { TrackActionsButton } from './track-actions';
import { TrackImportDialog } from './track-import-dialog';
import { TrackImportResultBanner } from './track-import-result-banner';
import { downloadTracksYaml } from './export-track';
import { trackImportResultSchema } from '../../schemas/tracks';
import {
  getImportResultStorageKey,
  readImportResult,
  storeImportResult,
} from '../import-result-storage';

const TRACK_IMPORT_RESULT_STORAGE_KEY = 'pulse.tracks.import-result';

const countChecks = (track: TrackDefinition): number =>
  track.levels.reduce((sum, level) => sum + level.checkIds.length, 0);

const getSortableValue = (
  item: TrackDefinition,
  column: string,
  labelByRef: Map<string, string>,
): string => {
  switch (column) {
    case 'name':
      return item.name;
    case 'levels':
      return String(item.levels.length);
    case 'checks':
      return String(countChecks(item));
    case 'status':
      return item.inactive ? 'Inactive' : 'Active';
    case 'ownerEntityRef':
      return getOwnerLabel(item.ownerEntityRef, labelByRef);
    default:
      return '';
  }
};

// The owner column renders catalog display names, so sorting has to use them too.
const createSortRows =
  (labelByRef: Map<string, string>) =>
  (data: TrackDefinition[], sort: SortDescriptor): TrackDefinition[] => {
    const direction = sort.direction === 'descending' ? -1 : 1;
    const sortColumn = String(sort.column);

    return [...data].sort((left, right) => {
      const comparison = sortCollator.compare(
        getSortableValue(left, sortColumn, labelByRef).trim(),
        getSortableValue(right, sortColumn, labelByRef).trim(),
      );

      if (comparison !== 0) {
        return comparison * direction;
      }

      return sortCollator.compare(left.id, right.id);
    });
  };

const createColumns = (
  getDetailsPath: ((params: { id: string }) => string) | undefined,
  onDeleted: () => void,
): ColumnConfig<TrackDefinition>[] => [
  {
    id: 'name',
    label: 'Name',
    isRowHeader: true,
    defaultWidth: '25fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={item.name}
        href={getDetailsPath ? getDetailsPath({ id: item.id }) : undefined}
      />
    ),
  },
  {
    id: 'levels',
    label: 'Levels',
    defaultWidth: '15fr',
    isSortable: true,
    cell: item => (
      <CellText title={`${item.levels.length} Levels`} color="secondary" />
    ),
  },
  {
    id: 'checks',
    label: 'Checks',
    defaultWidth: '15fr',
    isSortable: true,
    cell: item => (
      <CellText title={`${countChecks(item)} Checks`} color="secondary" />
    ),
  },
  {
    id: 'status',
    label: 'Status',
    defaultWidth: '15fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={item.inactive ? 'Inactive' : 'Active'}
        leadingIcon={
          <RiCircleFill
            size={9}
            color={
              item.inactive ? 'var(--bui-fg-warning)' : 'var(--bui-fg-positive)'
            }
          />
        }
        color={item.inactive ? 'secondary' : 'primary'}
      />
    ),
  },
  {
    id: 'ownerEntityRef',
    label: 'Owner',
    defaultWidth: '25fr',
    isSortable: true,
    cell: item => (
      <Cell>
        <EntityRefLink entityRef={item.ownerEntityRef} underline="none" />
      </Cell>
    ),
  },
  {
    id: 'actions',
    label: '',
    defaultWidth: '5fr',
    cell: item => (
      <Cell>
        <Flex justify="end">
          <TrackActionsButton track={item} onDeleted={onDeleted} />
        </Flex>
      </Cell>
    ),
  },
];

type TracksTableProps = {
  data: TrackDefinition[] | undefined;
  error: Error | undefined;
  columns: ColumnConfig<TrackDefinition>[];
};

const TracksTable = ({ data, error, columns }: TracksTableProps) => {
  const [selectedOwners, setSelectedOwners] = useUrlMultiSelect('owner');
  const [selectedKinds, setSelectedKinds] = useUrlMultiSelect('kind');
  const [searchQuery, setSearchQuery] = useUrlValue('search');
  const resetFilters = useResetUrlParams();
  const { labelByRef } = useMyTeams();

  const ownerOptions = useMemo(
    () => getOwnerOptions(data ?? [], labelByRef),
    [data, labelByRef],
  );
  const kindOptions = useMemo(() => getAffectedKindOptions(data ?? []), [data]);

  const filteredData = useMemo(() => {
    const owners = new Set(selectedOwners);
    const query = searchQuery.trim().toLowerCase();
    return (data ?? []).filter(item => {
      const matchesOwner = owners.size === 0 || owners.has(item.ownerEntityRef);
      const matchesSearch =
        query.length === 0 ||
        item.name.toLowerCase().includes(query) ||
        (item.description?.toLowerCase().includes(query) ?? false) ||
        item.ownerEntityRef.toLowerCase().includes(query) ||
        getOwnerLabel(item.ownerEntityRef, labelByRef)
          .toLowerCase()
          .includes(query);
      return (
        matchesOwner &&
        matchesAffectedKinds(item, selectedKinds) &&
        matchesSearch
      );
    });
  }, [data, selectedOwners, selectedKinds, searchQuery, labelByRef]);

  const sortRows = useMemo(() => createSortRows(labelByRef), [labelByRef]);

  const { tableProps } = useTable<TrackDefinition>({
    mode: 'complete',
    data: filteredData,
    initialSort: { column: 'name', direction: 'ascending' },
    sortFn: sortRows,
    paginationOptions,
  });

  return (
    <Flex direction="column" gap="6">
      <DefinitionListFilterBar
        ownerLabel="Track owner"
        ownerOptions={ownerOptions}
        selectedOwners={selectedOwners}
        onOwnersChange={setSelectedOwners}
        kindOptions={kindOptions}
        selectedKinds={selectedKinds}
        onKindsChange={setSelectedKinds}
        search={{ value: searchQuery, onChange: setSearchQuery }}
        onClear={() => resetFilters(['owner', 'kind', 'search'])}
      />
      <Table
        columnConfig={columns}
        {...tableProps}
        emptyState="No tracks found"
        error={error}
      />
    </Flex>
  );
};

export const PulseTracksPage = () => {
  const identityApi = useApi(identityApiRef);
  const [importResultStorageKey, setImportResultStorageKey] =
    useState<string>();
  const [importResult, setImportResult] = useState<TrackImportResult>();
  const { value: tracks, loading, error, retry } = usePulseTracks();
  const getDetailsPath = useRouteRef(tracksDetailsSubRouteRef);
  const getNewPath = useRouteRef(tracksNewSubRouteRef);
  const { canCreate } = useCreateTrack();
  const columns = createColumns(getDetailsPath, retry);

  useEffect(() => {
    let active = true;
    setImportResult(undefined);
    setImportResultStorageKey(undefined);

    const loadImportResult = async (): Promise<void> => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        if (!active) {
          return;
        }

        const storageKey = getImportResultStorageKey(
          TRACK_IMPORT_RESULT_STORAGE_KEY,
          identity.userEntityRef,
        );
        globalThis.sessionStorage?.removeItem(TRACK_IMPORT_RESULT_STORAGE_KEY);
        setImportResultStorageKey(storageKey);
        setImportResult(readImportResult(storageKey, trackImportResultSchema));
      } catch {
        // Without an authenticated identity, no persisted result is safe to show.
      }
    };

    void loadImportResult();
    return () => {
      active = false;
    };
  }, [identityApi]);

  const importDialog = canCreate ? (
    <TrackImportDialog
      onImportStarted={() => {
        setImportResult(undefined);
        storeImportResult(importResultStorageKey);
      }}
      onImported={result => {
        setImportResult(result);
        storeImportResult(importResultStorageKey, result);
        if (result.imported.length > 0) {
          retry();
        }
      }}
    />
  ) : null;

  return (
    <>
      {importResult ? (
        <Box px="4">
          <TrackImportResultBanner
            result={importResult}
            onDismiss={() => {
              setImportResult(undefined);
              storeImportResult(importResultStorageKey);
            }}
          />
        </Box>
      ) : null}
      <Header
        title="Tracks"
        description="Tracks group related checks into ordered levels, measuring how far each entity has progressed toward a maturity or compliance goal."
        customActions={
          <Flex gap="2">
            <Button
              variant="secondary"
              iconStart={<RiDownloadLine />}
              isDisabled={!tracks?.length}
              onClick={() => downloadTracksYaml(tracks ?? [])}
            >
              Export tracks
            </Button>
            {importDialog}
            {canCreate && (
              <ButtonLink
                variant="primary"
                iconStart={<RiAddLine />}
                href={getNewPath?.() ?? ''}
              >
                Create track
              </ButtonLink>
            )}
          </Flex>
        }
      />
      <Container>
        {loading ? (
          <PulseListSkeleton />
        ) : (
          <TracksTable data={tracks} error={error} columns={columns} />
        )}
      </Container>
    </>
  );
};
